// LanguagePicker — replaces the legacy vertical <Select> in the
// header with a Popover whose content is a horizontally-scrollable
// flag-pill rail. Same UX shape on both mobile and desktop: users
// see all supported languages in a single horizontal sweep instead
// of a tall dropdown.
import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { LANGUAGES } from "../lib/constants";

export default function LanguagePicker({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const current = LANGUAGES.find((l) => l.code === value) || LANGUAGES[0];

  const handlePick = (code) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-testid="language-selector"
        aria-label="Choose language"
        className="relative inline-flex items-center gap-1.5 h-9 px-2.5 md:px-3 rounded-full border border-cream-300 bg-white/60 hover:bg-white text-xs font-semibold text-ink outline-none focus:ring-2 focus:ring-forest/40"
      >
        <span className="text-sm leading-none">{current.flag}</span>
        <span className="hidden md:inline truncate max-w-[80px]">{current.label}</span>
        <CaretDown size={10} weight="bold" className="opacity-60" />
        {/* Tiny pulsing dot — confirms which language is active at a
            glance. Hides itself for English so the chip stays clean
            for the default-locale 80% case. */}
        {value && value !== "en" && (
          <span
            data-testid="language-active-dot"
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-terracotta shadow-[0_0_0_2px_rgba(245,239,224,1)] before:content-[''] before:absolute before:inset-0 before:rounded-full before:bg-terracotta before:animate-ping before:opacity-60"
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        data-testid="language-picker-rail"
        className="p-1.5 rounded-2xl border border-cream-300 bg-cream-100/95 backdrop-blur-md shadow-xl w-[min(86vw,520px)]"
      >
        <div
          className="flex items-center gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
          style={{ touchAction: "pan-x" }}
        >
          {LANGUAGES.map((l) => {
            const active = l.code === value;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => handlePick(l.code)}
                data-testid={`lang-${l.code}`}
                aria-pressed={active}
                title={l.label}
                className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "bg-forest text-cream shadow-sm"
                    : "bg-cream-200 text-ink hover:bg-cream-300"
                }`}
              >
                <span className="text-sm leading-none">{l.flag}</span>
                {l.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
